import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  output,
  viewChildren,
  input
} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import { TranscriptCue } from '../../models/classes/Transcript';

@Component({
  selector: 'hypecast-transcript-view',
  templateUrl: './transcript-view.component.html',
  styleUrls: ['./transcript-view.component.scss'],
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranscriptViewComponent implements OnChanges {
  readonly cues = input<TranscriptCue[]>([]);
  readonly currentTimeMs = input(0);

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['currentTimeMs']) {
      return;
    }
    const next = this.computeActiveIndex();
    if (next !== this.activeIndex) {
      this.activeIndex = next;
      this.scheduleScroll();
    }
  }

  readonly seek = output<number>();

  readonly cueEls = viewChildren<ElementRef<HTMLElement>>('cueEl');

  activeIndex = -1;
  private userScrolledUntil = 0;

  /**
   * Index of the active cue: the last cue that has started (startMs <= t), or -1
   * before the first cue starts. A cue stays active through the silence after it
   * until the next cue begins, so the highlight never flickers off in the gaps
   * between cues (which read as lag/jumps when playback crosses pauses).
   */
  computeActiveIndex(): number {
    const t = this.currentTimeMs() ?? 0;
    let active = -1;
    for (let i = 0; i < this.cues().length; i++) {
      if (t >= this.cues()[i].startMs) {
        active = i;
      } else {
        break;
      }
    }
    return active;
  }

  /** Show a speaker label only when the speaker differs from the previous cue. */
  showSpeaker(index: number): boolean {
    if (index <= 0) {
      return true;
    }
    return this.cues()[index].speaker !== this.cues()[index - 1].speaker;
  }

  onCueClick(cue: TranscriptCue): void {
    this.seek.emit(Math.round(cue.startMs / 1000));
  }

  /** While our own smooth scroll is animating, scroll events are not user scrolls. */
  private suppressScrollUntil = 0;

  /** Pause auto-scroll briefly after the user scrolls manually. */
  onUserScroll(): void {
    // Programmatic scrollTo() also fires scroll events on the container; treating
    // those as user scrolls would re-arm the pause on every auto-scroll and
    // permanently disable following the playback position.
    if (this.now() < this.suppressScrollUntil) {
      return;
    }
    this.userScrolledUntil = this.now() + 4000;
  }

  /**
   * Coalesce auto-scrolls to one per animation frame. scrollActiveIntoView
   * forces a synchronous reflow (rect reads) on a grid with thousands of
   * children; scrubbing the slider changes the active index once per slider
   * step, and running the reflow for every step blocked the main thread.
   */
  private scrollRafPending = false;

  private scheduleScroll(): void {
    if (this.scrollRafPending) {
      return;
    }
    this.scrollRafPending = true;
    requestAnimationFrame(() => {
      this.scrollRafPending = false;
      this.scrollActiveIntoView();
    });
  }

  private scrollActiveIntoView(): void {
    if (this.activeIndex < 0 || this.now() < this.userScrolledUntil) {
      return;
    }
    const el = this.cueEls()?.at(this.activeIndex)?.nativeElement;
    // Scroll WITHIN the transcript container only — never bubble up to the page
    // (the mini-player sits in a fixed bar, where scrollIntoView would shift the
    // whole layout and let the highlight drift out of the small panel).
    const container = el?.parentElement;
    if (!el || !container) {
      return;
    }
    // Rect-based offset: el.offsetTop is relative to the nearest POSITIONED
    // ancestor, which in embedded contexts (episode list rows) is not the
    // transcript container — that skewed the target and pushed the active cue
    // out of the visible panel.
    const delta = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    // Park the active cue near the top of the panel with exactly ~3 lines of
    // context above it, so most of the visible space shows upcoming lines.
    const lineHeight = parseFloat(getComputedStyle(container).lineHeight) || 26;
    const target = container.scrollTop + delta - 3 * lineHeight;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const top = Math.max(0, Math.min(target, maxScroll));
    this.suppressScrollUntil = this.now() + 800;
    // Instant scroll on purpose: smooth scrollTo() animations get cancelled by the
    // continuous change-detection/layout work while the player ticks (every 200ms),
    // so they silently never move the container.
    container.scrollTop = top;
  }

  // Wrapped so tests can stay deterministic without faking timers globally.
  private now(): number {
    return performance.now();
  }
}
